import { PlanService } from '@/services/Plan.service';
import formatters from '@/utils/Formatters';
import { Link, Stack, Typography } from '@mui/material';
import { grey } from '@mui/material/colors';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';

import dayjs from 'dayjs';
import { FC } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import * as uuid from 'uuid';

interface PendingTransactionsProps {
  sponsorPlanId: number;
}

const isTest = process.env.NODE_ENV === 'test';

const columns: GridColDef[] = [
  {
    field: 'createdAt',
    headerName: 'Date',
    valueFormatter: params => dayjs(params.value).format('M/DD/YYYY h:mm'),
    width: 150
  },
  { field: 'source', headerName: 'Source', width: 130 },
  { field: 'type', headerName: 'Description', width: 130 },
  {
    align: 'right',
    field: 'amount',
    headerAlign: 'right',
    headerName: 'Amount',
    valueFormatter: params => formatters.formatDollars(params.value),
    width: 150
  },
  {
    field: 'ucid',
    flex: 1,
    headerName: 'UCID',
    renderCell: params => (
      <Link
        component={RouterLink}
        target='_blank'
        to={`/plans/${params.row?.planId}/contributions/${params.value}/overview`}
        underline='hover'>
        {params.value}
      </Link>
    )
  }
];

export const PendingTransactions: FC<PendingTransactionsProps> = props => {
  const unallocatedFundsStatuses = useQuery(
    [PlanService.getUnallocatedFundsStatuses.name, props.sponsorPlanId],
    () => PlanService.getUnallocatedFundsStatuses(props.sponsorPlanId),
    {
      enabled: !!props.sponsorPlanId,
      refetchOnMount: 'always',
      select: data =>
        [
          ...(data.forfeitureTrackers?.map(forfeiture => ({
            ...forfeiture.data?.attributes,
            id: uuid.v4(),
            planId: props.sponsorPlanId,
            source: 'Forfeiture'
          })) ?? []),
          ...(data.suspenseTrackers?.map(suspense => ({
            ...suspense.data?.attributes,
            id: uuid.v4(),
            planId: props.sponsorPlanId,
            source: 'Suspense'
          })) ?? [])
        ]?.filter(item => item.status === 'NEW') ?? []
    }
  );

  return (
    !!unallocatedFundsStatuses.data?.length && (
      <Stack
        border={1}
        borderColor={grey[300]}
        borderRadius={1}
        data-testid='pendingTransactionsSection'>
        <Stack p={2}>
          <Typography data-testid='pendingTransactionsTitle' variant='h5'>
            Pending Transactions
          </Typography>
        </Stack>
        <DataGridPro
          autoHeight
          columns={columns}
          data-testid='pendingTransactionsTable'
          disableColumnMenu
          disableRowSelectionOnClick
          disableVirtualization={isTest}
          initialState={{
            pagination: {
              paginationModel: {
                page: 0,
                pageSize: 10
              }
            }
          }}
          loading={
            unallocatedFundsStatuses.isLoading ||
            unallocatedFundsStatuses.isFetching
          }
          pageSizeOptions={[10, 25, 50]}
          pagination
          paginationMode='client'
          rows={unallocatedFundsStatuses.data}
        />
      </Stack>
    )
  );
};
